import { useState, useCallback } from 'react';
import { compressImage, type ImageCompressionOptions } from '@/utils/imageCompressionService';
import { toast } from 'sonner';

interface UseImageCompressionReturn {
  compressedImage: Blob | null;
  isCompressing: boolean;
  compressionError: Error | null;
  compressImageFile: (file: File, options?: ImageCompressionOptions) => Promise<Blob | null>;
  reset: () => void;
}

/**
 * Hook pour compresser les images avant upload
 * Optimise automatiquement les images pour réduire la taille de page
 */
export const useImageCompression = (): UseImageCompressionReturn => {
  const [compressedImage, setCompressedImage] = useState<Blob | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionError, setCompressionError] = useState<Error | null>(null);

  const compressImageFile = useCallback(async (
    file: File,
    options?: ImageCompressionOptions
  ): Promise<Blob | null> => {
    setIsCompressing(true);
    setCompressionError(null);

    try {
      // Vérifier la taille du fichier original
      const originalSizeKB = file.size / 1024;
      
      // Options par défaut ultra-agressives pour économiser la bande passante
      const defaultOptions: ImageCompressionOptions = {
        maxWidth: 1920,
        maxHeight: 1080,
        quality: 0.55, // Qualité ultra-basse pour économie maximale
        format: 'webp',
        maintainAspectRatio: true
      };

      const finalOptions = { ...defaultOptions, ...options };

      // Compresser l'image
      const compressed = await compressImage(file, finalOptions);
      const compressedSizeKB = compressed.size / 1024;
      
      // Calculer le taux de compression
      const compressionRatio = ((originalSizeKB - compressedSizeKB) / originalSizeKB * 100).toFixed(1);
      
      console.log(`Image compressée: ${originalSizeKB.toFixed(1)}KB → ${compressedSizeKB.toFixed(1)}KB (${compressionRatio}% de réduction)`);

      // Afficher un message de succès si la compression est significative
      if (parseFloat(compressionRatio) > 20) {
        toast.success(`Image optimisée: ${compressionRatio}% plus légère`);
      }

      setCompressedImage(compressed);
      return compressed;
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Compression failed');
      setCompressionError(err);
      console.error('Image compression error:', err);
      toast.error('Erreur lors de la compression de l\'image');
      return null;
    } finally {
      setIsCompressing(false);
    }
  }, []);

  const reset = useCallback(() => {
    setCompressedImage(null);
    setCompressionError(null);
    setIsCompressing(false);
  }, []);

  return {
    compressedImage,
    isCompressing,
    compressionError,
    compressImageFile,
    reset
  };
};
